import React, { useState } from "react"
import Slider from "../slider"
import Label from "../label"

export type PixelSliderProps = {
  /** Text label displayed above the slider */
  label?: string
  /** Input name and id attribute */
  name: string
  /** Current numeric value */
  value: number
  /** Called when the value changes */
  onChange: (value: number) => void
  /** Minimum allowed value */
  min?: number
  /** Maximum allowed value */
  max: number
  /** Step increment between values */
  step?: number
  /** Whether to display the unit suffix in the input */
  showUnit?: boolean
  /** Unit suffix displayed in the input (e.g. "px", "em", "%") */
  unit?: string
}

/**
 * A slider with an accompanying numeric input field, useful for pixel-based
 * or unit-based value selection. Displays a configurable unit suffix.
 */
function PixelSlider({
  label,
  name,
  value,
  onChange,
  min = 0,
  max,
  step,
  showUnit = true,
  unit = "px"
}: PixelSliderProps) {
  const [sliderValue, setSliderValue] = useState<number | string>(value)

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value
    const numericValue = Number(newValue)

    if (isNaN(numericValue)) return

    setSliderValue(newValue) // We keep the local state in string so we can handle float values being typed
    onChange(numericValue)
  }

  return (
    <div>
      {label && (
        <Label className="mb-1" htmlFor={name}>
          {label}
        </Label>
      )}

      <div className="grid grid-cols-3 gap-4">
        <Slider
          className="col-span-2"
          value={Number(sliderValue)}
          onChange={handleChange}
          min={min}
          max={max}
          step={step}
        />

        <div className="w-full relative">
          <input
            id={name}
            name={name}
            className="block border rounded w-full p-2 border-gray-200 text-gray-700 text-sm focus:outline-blue"
            value={sliderValue}
            onChange={handleChange}
            min={min}
            max={max}
            style={{
              lineHeight: "22px",
              paddingRight: showUnit ? "40px" : undefined
            }}
          />
          {showUnit && (
            <span className="absolute right-0 inset-y-0 flex items-center p-2 text-gray-700 text-sm pointer-events-none">
              {unit}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export { PixelSlider }
export default PixelSlider
